import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import WizardContent from '~/layouts/WizardContent';
import { ConfirmButton } from '~/layouts/ksa/CompanyInfoLayout';
import { useSelectStep, useSubmitStep } from '~/controllers/wizard';
import { message } from 'antd';

import { useDirection } from '~/hooks/i18nHooks';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { BackArrowIcon } from '~/img';
import { usePageTitle } from '~/context/PageTitleContext';

const SERVICES_STORAGE_KEY = 'ksa-services-submit-values';

const STEPS = [
  { label: 'Basic Information', desc: 'Mobile number, CR, and National ID/Iqama' },
  { label: 'Company Details', desc: 'Auto-filled from official records' },
  { label: 'Bank Details Start', desc: 'Add your IBAN securely' },
  { label: 'Agreement Start', desc: 'Review and sign digitally' },
  { label: 'Identity Verification', desc: 'Secure verification via Nafath' },
];

const BackArrowSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M13.3333 25.3326L4 15.9993L13.3333 6.66602M4 15.9993H28" stroke="#1C2027" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LockSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
    <path d="M4.75 6.75V5.55C4.75 3.86984 4.75 3.02976 5.07698 2.38803C5.3646 1.82354 5.82354 1.3646 6.38803 1.07698C7.02976 0.75 7.86984 0.75 9.55 0.75H9.95C11.6302 0.75 12.4702 0.75 13.112 1.07698C13.6765 1.3646 14.1354 1.82354 14.423 2.38803C14.75 3.02976 14.75 3.86984 14.75 5.55V6.75M9.75 12.75V14.75M7.15 20.75H12.35C14.5902 20.75 15.7103 20.75 16.566 20.314C17.3186 19.9305 17.9305 19.3186 18.314 18.566C18.75 17.7103 18.75 16.5902 18.75 14.35V13.15C18.75 10.9098 18.75 9.78968 18.314 8.93404C17.9305 8.18139 17.3186 7.56947 16.566 7.18597C15.7103 6.75 14.5902 6.75 12.35 6.75H7.15C4.90979 6.75 3.78968 6.75 2.93404 7.18597C2.18139 7.56947 1.56947 8.18139 1.18597 8.93404C0.75 9.78968 0.75 10.9098 0.75 13.15V14.35C0.75 16.5902 0.75 17.7103 1.18597 18.566C1.56947 19.3186 2.18139 19.9305 2.93404 20.314C3.78968 20.75 4.90979 20.75 7.15 20.75Z" stroke="#1C2027" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const StartInMinutes: React.FC = () => {
  const { t, i18n } = useTranslation('ksaApplication');
  const submitMutation = useSubmitStep();
  const selectStepMutation = useSelectStep();
  const [isLoading, setIsLoading] = useState(false);
  const dir = useDirection();
  const isMobile = useIsMobile();
  const isRTL = dir === 'rtl';
  const { setPageTitle } = usePageTitle();

  // Register this page's title so event_pageview fires with the correct page title
  useEffect(() => {
    setPageTitle(t('Start in minutes'));
    return () => setPageTitle('');
  }, [t]);

  const isDisabled =
    isLoading || submitMutation.isLoading || selectStepMutation.isLoading;

  const handleStartNow = useCallback(async () => {
    setIsLoading(true);
    try {
      const raw = sessionStorage.getItem(SERVICES_STORAGE_KEY);
      if (!raw) {
        void message.error(t('Something went wrong. Please go back and try again.'));
        return;
      }

      // Fire form_submit event when the user clicks "Start Now"
      (window as any).dataLayer.push({
        event: 'event_formsubmit',
        page_ln: i18n.language?.slice(0, 2) || 'en',
        page_name: window.location.href,
        page_source: document.referrer || 'direct',
        page_section: 'Start Now',
      });

      const data = JSON.parse(raw);

      // Strip internal `terminalFee` key — only `terminals` is needed by the backend
      if (data?.services && typeof data.services === 'object') {
        Object.values(data.services).forEach((service: any) => {
          if (service && typeof service === 'object') {
            delete service.terminalFee;
          }
        });
      }

      await submitMutation.mutateAsync(data);
      sessionStorage.removeItem(SERVICES_STORAGE_KEY);
    } catch {
      void message.error(t('Something went wrong. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [submitMutation, t]);

  const handleBack = useCallback(() => {
    sessionStorage.removeItem(SERVICES_STORAGE_KEY);
    selectStepMutation.mutate({ stepKey: 'services' });
  }, [selectStepMutation]);

  return (
    <div style={{ width: "100%" }}>
      <StyledWizardContent
        content={
          <ContentWrap dir={dir}>

            {/* ── Title row with back arrow ── */}
            <TitleRow dir={dir}>
              <BackArrow
                id="startInMinutesBackArrow"
                onClick={handleBack}
                disabled={isDisabled}
                aria-label={t('Back')}
              >
                <BackArrowIcon
                  style={{ transform: dir === 'rtl' ? 'scaleX(-1)' : undefined }}
                />
              </BackArrow>
              <PageTitle dir={dir}>{t('Start in minutes')}</PageTitle>
            </TitleRow>
            <PageSubtitle dir={dir}>
              {t('Complete your onboarding in a few simple steps, no documents required.')}
            </PageSubtitle>

            {!isMobile && <TitleDivider />}

            {/* ── Steps box ── */}
            <StepsBox>
              <StepsTitle dir={dir}>{t('Steps')}</StepsTitle>
              {STEPS.map(({ label, desc }) => (
                <Step key={label} dir={dir}>
                  <StepLabel dir={dir}>{t(label)}</StepLabel>
                  <StepDesc dir={dir}>{t(desc)}</StepDesc>
                </Step>
              ))}
            </StepsBox>

            {/* ── Security note ── */}
            <SecurityNote dir={dir}>
              <LockIcon>{LockSVG}</LockIcon>
              <span>
                {t('All your data is protected in accordance with the highest security standards and regulations adopted in the Kingdom of Saudi Arabia.')}
              </span>
            </SecurityNote>

            {/* ── CTA ── */}
            <CtaWrapper>
              <ConfirmButton
                id="startNowButton"
                type="primary"
                loading={isLoading || submitMutation.isLoading}
                disabled={isDisabled}
                onClick={handleStartNow}
              >
                {t('Start Now')}
              </ConfirmButton>
            </CtaWrapper>

          </ContentWrap>
        }
      />
    </div>
  );
};

/* ── Prop type helpers ── */
interface DirProp { dir?: string; }

/* ── Shared CSS helpers ── */
const rtlFont = ({ dir }: DirProp) =>
  css`font-family: ${dir === 'rtl' ? 'Tajawal' : 'Inter'}, sans-serif;`;

const textAlign = ({ dir }: DirProp) =>
  css`text-align: ${dir === 'rtl' ? 'right' : 'left'};`;

/* ── Styled components ── */
const ContentWrap = styled.div<DirProp>`
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  direction: ${({ dir }) => dir ?? 'ltr'};
`;

const TitleRow = styled.div<DirProp>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const BackArrow = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 24px;
  color: #1c2027;
  display: flex;
  align-items: center;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.6; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const PageTitle = styled.div<DirProp>`
  ${rtlFont}
  ${textAlign}
  overflow: hidden;
  color: #070707;
  text-overflow: ellipsis;
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 38px;
  font-style: normal;
  font-weight: 500;
  line-height: 56px;
  flex: 1;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const PageSubtitle = styled.p<DirProp>`
  ${rtlFont}
  ${textAlign}
  color: #000;
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.14px;
  margin: 0 0 20px 0;

  @media (max-width: 768px) {
    font-size: 16px;
    letter-spacing: 0.16px;
  }
`;

const TitleDivider = styled.hr`
  border: none;
  border-top: 1px solid #DADADA;
  margin: 0 0 28px 0;
`;

const StepsBox = styled.div`
  background-color: #E1EFFF;
  border-radius: 12px;
  padding: 24px 32px;
  margin-bottom: 28px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const StepsTitle = styled.h3<DirProp>`
  ${rtlFont}
  ${textAlign}
  font-size: 24px;
  font-weight: 500;
  color: #000;
  margin: 0 0 20px 0;
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Step = styled.div<DirProp>`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 16px;
  &:last-child { margin-bottom: 0; }
`;

const StepLabel = styled.p<DirProp>`
  ${rtlFont}
  ${textAlign}
  width: 100%;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  color: #000;
  margin: 0 0 0px 0;
`;

const StepDesc = styled.p<DirProp>`
  ${rtlFont}
  ${textAlign}
  width: 100%;
  font-size: 14px;
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
`;

const SecurityNote = styled.div<DirProp>`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  ${rtlFont}
  ${textAlign}
  margin-bottom: 32px;
  width: 100%;

  span {
    color: #000;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.14px;

    @media (max-width: 768px) {
      font-size: 16px;
      letter-spacing: 0.16px;      
    }
  }
`;

const LockIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CtaWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  @media (max-width: 768px) {
   margin-top :70px;
  }
`;

const StyledWizardContent = styled(WizardContent)`
  .MainContent {
    display: flex !important;
    width: 1080px !important;
    max-width: 100% !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding: 32px 36px !important;
    justify-content: center !important;
    align-items: flex-start !important;
    gap: 24px !important;
    border-radius: 16px !important;
    background: #fff !important;
  }
  /* Back arrow: restore natural icon-button size */
  .MainContent #startInMinutesBackArrow {
    width: 32px !important;
    max-width: 32px !important;
    min-width: unset !important;
    height: 32px !important;
    min-height: unset !important;
    border-radius: 0 !important;
    padding: 0 !important;
    background: none !important;
    box-shadow: none !important;
  }
  /* Start Now button — Figma spec: 260px wide, 12px/48px padding, 12px radius */
  .MainContent #startNowButton {
    width: 260px !important;
    max-width: 260px !important;
    min-width: unset !important;
    height: auto !important;
    min-height: unset !important;
    padding: 12px 48px !important;
    border-radius: 12px !important;
    background: #0069b1 !important;
    border-color: #0069b1 !important;
    font-weight: 600 !important;
    font-size: 16px !important;
  }
`;

export default StartInMinutes;